import { useMemo } from 'react'
import { useSnapshot } from 'valtio'
import type { Item, ItemRelease } from '../../../types'
import { state } from './state'

// Helper to parse ISO date as local date
function parseLocalDate(isoDateStr: string) {
  const [year, month, day] = isoDateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export const getLatestRelease = (item: Item) => {
  return item.releases.reduce((latest, current) => {
    if (!latest) return current
    return parseLocalDate(current.releaseDate) >
      parseLocalDate(latest.releaseDate)
      ? current
      : latest
  }, item.releases[0])
}

export const getReleaseDates = (): string[] => {
  const { radarItems } = useSnapshot(state)
  return useMemo(
    () =>
      Array.from(
        new Set(
          radarItems.flatMap((item: Item) =>
            item.releases.map((release: ItemRelease) => release.releaseDate)
          )
        )
      ),
    [radarItems]
  )
}

export const getTags = (items: readonly Item[]): string[] => {
  return Array.from(
    new Set(
      items.flatMap((item: Item) =>
        ((item.tags || []) as readonly string[]).map(tag => tag.toUpperCase())
      )
    )
  )
}

export const sortByTitle = (a: Item, b: Item) => {
  const getLatestTitle = (item: Item) => {
    if (!item.releases || item.releases.length === 0) return ''
    const sortedReleases = [...item.releases].sort(
      (x: ItemRelease, y: ItemRelease) =>
        parseLocalDate(y.releaseDate).getTime() -
        parseLocalDate(x.releaseDate).getTime()
    )
    const latestRelease = sortedReleases[0]
    return latestRelease.title
  }

  return getLatestTitle(a).localeCompare(getLatestTitle(b))
}

export const groupItemsByRing = (items: readonly Item[]) => {
  const { radarConfig } = useSnapshot(state)
  const rings = radarConfig?.rings || []
  const toggles = radarConfig?.toggles || {}
  const showEmptyRings = toggles.showEmptyRings

  return rings.reduce(
    (acc, ring) => {
      const ringItems = items.filter(item => {
        const latestRelease = item.releases.reduce((latest, current) => {
          if (!latest) return current
          return parseLocalDate(current.releaseDate) >
            parseLocalDate(latest.releaseDate)
            ? current
            : latest
        }, item.releases[0])
        return latestRelease.ring === ring.id
      }) as Item[]
      if (ringItems.length || showEmptyRings) acc[ring.id] = ringItems
      return acc
    },
    {} as { [ringId: string]: Item[] }
  )
}

export const groupItemsByQuadrant = (items: readonly Item[]) => {
  const { radarConfig } = useSnapshot(state)
  const quadrants = radarConfig?.quadrants || []

  return quadrants.reduce(
    (acc, quadrant) => {
      const quadrantItems = items.filter(item => item.quadrant === quadrant.id)
      if (quadrantItems.length) acc[quadrant.id] = quadrantItems
      return acc
    },
    {} as { [quadrantId: string]: Item[] }
  )
}
